import type { CostReport } from "@botallion/metering";
import type { CostLabel } from "@botallion/metering/hooks/dashboard";
import type { DashboardState } from "../state.ts";
import type { StackView } from "../stack.ts";
import { html, type Html } from "../html.ts";

/** What the person at the screen can do, and what the page remembers between renders. */
export interface ViewContext {
  state: DashboardState;
  /** Current time for "how long ago": wall clock live, the recording's clock in replay. */
  now: number;
  /** Who would approve, and whether they can right now. */
  approver: { handle: string | null; canApprove: boolean; why: string | null };
  /** Draft answers to plan questions, by `<specId>/<questionId>`. */
  answers: Record<string, string>;
  /** Actions in flight, by action key, so a button can show it is working. */
  busy: ReadonlySet<string>;
  /** Overlap cards the viewer opened or closed by hand. */
  expanded: Readonly<Record<string, boolean>>;
  /** The stack the deploys view describes, names and sources only. */
  stack: StackView | null;
  /** Live mode: model costs from the coordinator's `GET /v1/costs` (WP-14), null until loaded. Absent elsewhere. */
  costs?: CostReport | null;
}

/** `00:41:00` (UTC) from an ISO timestamp. */
export function clock(ts: string | null | undefined): string {
  if (!ts) return "--:--:--";
  return ts.slice(11, 19);
}

/** `12s ago`, `3m ago`, `1h 5m ago`. */
export function ago(now: number, ts: string): string {
  const s = Math.max(0, Math.round((now - Date.parse(ts)) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ${m % 60}m ago`;
}

export type Tone = "info" | "warn" | "block" | "good" | "human" | "muted";

export function chip(text: string, tone: Tone = "muted", title?: string): Html {
  return html`<span class="chip ${tone}"${title ? html` title="${title}"` : ""}>${text}</span>`;
}

export function panel(id: string, title: string, body: Html, extra?: Html): Html {
  return html`<section class="panel" id="panel-${id}"><header><h2>${title}</h2>${extra ?? ""}</header><div class="body">${body}</div></section>`;
}

export function empty(text: string): Html {
  return html`<p class="empty">${text}</p>`;
}

/** A cost chip (WP-14), with the breakdown and price date on hover. */
export function costChip(label: CostLabel): Html {
  return chip(label.text, label.tone, label.title);
}

export const shortSha = (sha: string | null | undefined): string => (sha ? sha.slice(0, 7) : "none");
